import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { IconPlayerPauseFilled, IconPlayerPlayFilled, IconPlayerTrackPrevFilled } from "@tabler/icons-react"
import { fmtTimestamp } from "../../helpers"
import classes from "../../styles.module.css"
import type { IProps } from "./interfaces"

/**
 * Fragment transport chip next to seekable analysis items. Renders a play/pause
 * toggle synced to the shared recording player plus the moment's timestamp;
 * once this fragment is the active one, a "restart this moment" rewind appears
 * alongside. Hidden when no segment matched (`seekTime === null`) or no
 * play handler is available. Stops event propagation so clicking a control
 * doesn't also trigger the surrounding row's handler (e.g. an EditableField
 * popup).
 */
export function SeekIcon({
  seekTime,
  active = false,
  playing = false,
  onPlayPause,
  onRewind,
}: IProps): React.JSX.Element | null {
  const { i18n } = useLingui()
  if (seekTime === null || !onPlayPause) return null
  const showPause = active && playing
  const restartLabel = i18n._(msg`Restart from this moment`)
  const toggleLabel = showPause ? i18n._(msg`Pause`) : i18n._(msg`Play from this moment`)
  return (
    <span className={classes.seekControl}>
      {active && onRewind && (
        <button
          type="button"
          className={classes.seekRewind}
          onClick={(e) => {
            e.stopPropagation()
            onRewind()
          }}
          title={restartLabel}
          aria-label={restartLabel}
        >
          <IconPlayerTrackPrevFilled size={11} />
        </button>
      )}
      <button
        type="button"
        className={classes.seekIcon}
        data-active={active ? "true" : "false"}
        onClick={(e) => {
          e.stopPropagation()
          onPlayPause()
        }}
        title={toggleLabel}
        aria-label={toggleLabel}
      >
        {showPause ? <IconPlayerPauseFilled size={11} /> : <IconPlayerPlayFilled size={11} />}
        <span className={classes.seekLabel}>{fmtTimestamp(seekTime)}</span>
      </button>
    </span>
  )
}
